import { Container, Graphics, Text, type Texture, type Ticker } from 'pixi.js';
import { ADULT_AGE } from '../../logic/age/ageCheck';
import { BoardPanel } from '../board/BoardPanel';
import { BrassButton } from '../board/BrassButton';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';
import { AttractReels, type AttractArt } from './AttractReels';

export type AgeGateStep = 'ask' | 'under';
export type AgeGateResult = 'ok' | 'under';

/** The gate as plain data, for tests and the host. */
export interface AgeGateUi {
  step: AgeGateStep;
  title: string;
  question: string;
  /** The two answers' labels while asking (no first, yes second); none once turned away. */
  answers: string[];
}

export interface AgeGateScreenOptions {
  art: AttractArt & { floor: Texture | null };
  ticker: Ticker;
  /** "under" when this device was turned away before. */
  state: 'ask' | 'under';
  onResult: (result: AgeGateResult) => void;
}

const MAX_BOARD_W = 380;
const PAD = { top: 32, side: 18, bottom: 20 };
const MARGIN = 12;
const BUTTON_H = 52;

const WELCOME = 'WELCOME';
const INTRO = 'A free game for adults. Just for fun: nothing to win or cash out.';
const QUESTION = `Are you ${ADULT_AGE} or over?`;
const YES = `YES, I'M ${ADULT_AGE}+`;
const NO = 'NO';

/**
 * The first-launch age check, drawn on the board kit over the game playing
 * itself: one question, two answers (producer 2026-11-03: one tap, where
 * the birth year on a keypad stopped new players before their first spin).
 * Only the answer is kept, on the device.
 */
export class AgeGateScreen extends Container {
  readonly attract: AttractReels;
  private readonly dim = new Graphics();
  private readonly card = new Container();
  private readonly board: BoardPanel;
  private readonly plaque = new TitlePlaque(WELCOME, 18);
  private readonly content = new Container();
  private readonly intro = new Text({ text: INTRO, style: { ...uiStyle(15, COLOURS.cream, '600'), align: 'center', wordWrap: true } });
  private readonly question = new Text({ text: QUESTION, style: { ...displayStyle(22), align: 'center', wordWrap: true } });
  private readonly body = new Text({ text: '', style: { ...uiStyle(16, COLOURS.cream, '600'), align: 'center', wordWrap: true } });
  private readonly yes: BrassButton;
  private readonly no: BrassButton;

  private step: AgeGateStep;
  private viewW = 1;
  private viewH = 1;
  private contentW = 300;
  private finished = false;

  constructor(private readonly options: AgeGateScreenOptions) {
    super();
    this.step = options.state === 'under' ? 'under' : 'ask';
    this.attract = new AttractReels(options.art, options.ticker);
    this.board = new BoardPanel({ floor: options.art.floor });
    // Taps on the dim never reach anything underneath.
    this.dim.eventMode = 'static';
    this.yes = new BrassButton({ label: YES, kind: 'gold', size: 20, onTap: () => this.answer(true) });
    this.no = new BrassButton({ label: NO, kind: 'wood', size: 18, onTap: () => this.answer(false) });
    this.content.addChild(this.intro, this.question, this.body, this.no, this.yes);
    this.card.addChild(this.board, this.content, this.plaque);
    this.addChild(this.attract, this.dim, this.card);
    if (this.step === 'under') this.closeGame();
    else this.attract.start();
    this.refresh();
  }

  layout(viewW: number, viewH: number): void {
    this.viewW = viewW;
    this.viewH = viewH;
    const boardW = Math.min(MAX_BOARD_W, Math.max(260, viewW - 2 * MARGIN));
    this.contentW = boardW - 2 * PAD.side;
    const contentH = this.layoutContent();
    const boardH = contentH + PAD.top + PAD.bottom;
    this.board.resize(boardW, boardH);
    this.content.position.set(PAD.side, PAD.top);
    this.plaque.position.set(boardW / 2, 0);
    const rise = this.plaque.plateHeight / 2;
    // Too short for the board (a phone on its side): shrink it to fit.
    const scale = Math.min(1, (viewH - 2 * MARGIN) / (boardH + rise));
    this.card.scale.set(scale);
    this.card.pivot.set(boardW / 2, 0);
    const cardH = (boardH + rise) * scale;

    // The game sits above the board when both fit, as on a tall phone;
    // otherwise behind it: centred on wide screens, at the top on narrow
    // ones so its top rows still show over the board.
    const wide = viewW >= 700;
    const machineW = Math.min(viewW - 2 * MARGIN, wide ? 900 : 620);
    const machineH = AttractReels.heightFor(machineW);
    const machineX = (viewW - machineW) / 2;
    let machineY: number;
    let cardTop: number;
    const free = viewH - machineH - cardH;
    if (this.step === 'under') {
      // No game for this player: only the woods behind the board.
      machineY = (viewH - machineH) / 2;
      cardTop = (viewH - cardH) / 2;
    } else if (!wide && free >= 3 * MARGIN) {
      machineY = free / 3;
      cardTop = machineY + machineH + free / 3;
    } else if (wide) {
      machineY = (viewH - machineH) / 2;
      cardTop = (viewH - cardH) / 2;
    } else {
      machineY = MARGIN;
      cardTop = viewH - cardH - MARGIN;
    }
    this.card.position.set(Math.round(viewW / 2), Math.round(cardTop + rise * scale));
    this.attract.layout(viewW, viewH, { x: machineX, y: machineY, width: machineW });
    this.drawDim();
  }

  /** The gate as plain data. */
  ui(): AgeGateUi {
    const asking = this.step === 'ask';
    return {
      step: this.step,
      title: asking ? WELCOME : 'SORRY',
      question: this.question.text,
      answers: asking ? [NO, YES] : [],
    };
  }

  /** Press an answer by what it says, or "yes" / "no". */
  press(label: string): boolean {
    const yes = this.isYes(label);
    if (yes === null) return false;
    return this.answer(yes);
  }

  /** Where on the canvas an answer sits (its centre), so a browser test
   *  can tap it for real. Null when it isn't showing. */
  where(label: string): { x: number; y: number } | null {
    const yes = this.isYes(label);
    if (yes === null || this.step !== 'ask') return null;
    const b = (yes ? this.yes : this.no).getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  /** A key from the keyboard: Y or N. Enter is not an answer. */
  key(key: string): boolean {
    if (key === 'y' || key === 'Y') return this.answer(true);
    if (key === 'n' || key === 'N') return this.answer(false);
    return false;
  }

  private isYes(label: string): boolean | null {
    const l = label.toLowerCase();
    if (l === 'yes' || label === YES) return true;
    if (l === 'no' || label === NO) return false;
    return null;
  }

  private answer(adult: boolean): boolean {
    if (this.finished || this.step !== 'ask') return false;
    this.finished = true;
    if (!adult) {
      this.step = 'under';
      this.closeGame();
      this.refresh();
      this.options.onResult('under');
      return true;
    }
    // Lift the gate off the game.
    void animate(320, (t) => {
      this.alpha = 1 - ease.inCubic(t);
    }).then(() => this.options.onResult('ok'));
    return true;
  }

  private closeGame(): void {
    this.attract.stop();
    this.attract.setMachineVisible(false);
  }

  /** Bring every label and button up to date, then lay out. */
  private refresh(): void {
    const asking = this.step === 'ask';
    this.plaque.setTitle(asking ? WELCOME : 'SORRY');
    this.intro.visible = asking;
    this.yes.visible = asking;
    this.no.visible = asking;
    this.body.visible = !asking;
    if (asking) this.question.text = QUESTION;
    else {
      this.question.text = 'Goodtime is for adults';
      this.body.text = `You need to be ${ADULT_AGE} or over to play. Thanks for stopping by.`;
    }
    if (this.viewW > 1) this.layout(this.viewW, this.viewH);
  }

  /** Place everything inside the board; returns the content's height. */
  private layoutContent(): number {
    const w = this.contentW;
    for (const t of [this.intro, this.question, this.body]) {
      t.style.wordWrapWidth = w;
      t.anchor.set(0.5, 0);
      t.x = w / 2;
    }
    if (this.step === 'under') {
      this.question.y = 4;
      this.body.y = this.question.y + this.question.height + 10;
      return this.body.y + this.body.height + 6;
    }
    this.intro.y = 0;
    this.question.y = this.intro.height + 14;
    const buttonsY = this.question.y + this.question.height + 18;
    // No on the left, narrow; the yes takes the rest, so its longer words fit a small phone.
    const gap = 10;
    const noW = Math.round(Math.min(110, (w - gap) * 0.36));
    this.no.resize(noW, BUTTON_H);
    this.yes.resize(w - gap - noW, BUTTON_H);
    this.no.position.set(0, buttonsY);
    this.yes.position.set(noW + gap, buttonsY);
    return buttonsY + BUTTON_H;
  }

  private drawDim(): void {
    const alpha = this.step === 'under' ? 0.72 : 0.36;
    this.dim.clear().rect(0, 0, this.viewW, this.viewH).fill({ color: 0x0a0602, alpha });
  }
}
